'use client';

import {
  absoluteBaseUrl,
  complianceDsarPageUrl
} from '~/constants/environment';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { useDealershipId } from '~/context/dealership/StaticContext';
import buildDealershipUrl from '~/utilities/dealership/buildDealershipUrl';
import {
  EffectiveDate,
  Table,
  TableCell,
  TableHeaderCell,
  Title,
  Wrapper
} from './PrivacyPolicy.styles';

declare global {
  interface Window {
    OneTrust?: {
      ToggleInfoDisplay: () => void;
    };
  }
}

const effectiveDate = 'June 29, 2026';

export default function ConsumerPrivacyNotice() {
  const dealershipId = useDealershipId();
  const trackEvent = useTrackEvent();

  const openPrivacyPreferences = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    trackEvent('Privacy Policy - Clicked Your Privacy Choices', {
      PrivacyPolicySection: 'browser-only-opt-out'
    });
    window?.OneTrust?.ToggleInfoDisplay();
  };

  return (
    <Wrapper>
      <Title>Carvana’s Consumer Privacy Notice</Title>
      <EffectiveDate>Effective Date: {effectiveDate}</EffectiveDate>

      <section id="consumer-privacy-notice" className="flex flex-col gap-y-lg">
        <p>
          <strong>A. Introduction</strong>
        </p>
        <p>
          Carvana Operations HC LLC (“Carvana HC”), together with its parents,
          subsidiaries, and affiliates, including ADESA US AUCTION, LLC
          (“ADESA”) (collectively, “Carvana ”) collects and processes
          information about you as described in this Consumer Privacy Notice
          (“Consumer Notice”). We are committed to protecting the privacy of
          those with whom we interact.
        </p>
        <p>
          This Consumer Notice contains details about how we collect, use, and
          share personal information that we obtain from and about you when you
          interact with us via our website(s), email, mobile application(s),
          social media accounts, online advertisements, and other digital
          platforms (collectively, the “Digital Properties”), and in other
          online and offline interactions. This includes how we handle Personal
          Information (as defined by applicable data protection laws) that is
          subject to the Applicable Privacy State Residents (as defined below)
          consumers and website visitors.
        </p>
        <p>
          This Consumer Notice does not apply to the below types of information
          that are subject to certain laws. Please refer to the applicable
          link(s) below for more information on how we process such information:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            <strong>
              Information we collect in connection with providing individual
              consumers a financial product or service (or in connection with a
              consumer seeking such financial products or services), such as by
              reviewing applications for financing is subject to our Financial
              Privacy Notice (which can be viewed by clicking{' '}
              <a
                href={buildDealershipUrl(
                  `${absoluteBaseUrl}/financial-and-other-privacy-notices`,
                  dealershipId
                )}
                className="text-text-primary underline"
                data-adobe-id="financial-privacy-notice"
                data-analytics-id={null}
                data-testid="financial-privacy-notice-link"
              >
                HERE
              </a>
              )
            </strong>{' '}
            instead of this Consumer Notice because that data is regulated under
            separate laws, such as the federal Gramm-Leach-Bliley Act (“GLBA”),
            Fair Credit Reporting Act (“FCRA”), or the California Financial
            Information Privacy Act (collectively, “Financial Information”).
          </li>
          <li>
            <strong>For California Business Contacts, </strong>such as our
            vendors, service providers, and business partners – click{' '}
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financial-and-other-privacy-notices#business-contact-privacy-notice`,
                dealershipId
              )}
              className="text-text-primary underline"
              data-adobe-id="business-contact-privacy-notice"
              data-analytics-id={null}
              data-testid="business-contact-privacy-notice-link"
            >
              HERE
            </a>{' '}
            to view our California Business (“B2B”) Privacy Notice
          </li>
          <li>
            <strong>For California Candidates, </strong>such as applicants –
            click{' '}
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financial-and-other-privacy-notices#applicant-privacy-notice`,
                dealershipId
              )}
              className="text-text-primary underline"
              data-adobe-id="applicant-privacy-notice"
              data-analytics-id={null}
              data-testid="applicant-privacy-notice-link"
            >
              HERE
            </a>{' '}
            to view our California Job Applicant Privacy Notice
          </li>
        </ul>
        <p>
          For more information regarding Carvana’s terms of use, please review
          our{' '}
          <a
            href="https://www.carvana.com/terms-of-use"
            className="text-text-primary underline"
            data-adobe-id="user-agreement-introduction"
            data-analytics-id={null}
            data-testid="user-agreement-link-introduction"
          >
            User Agreement
          </a>
          , the terms of which are incorporated herein.
        </p>
        <p>
          <strong>Changes:</strong> We may update this Consumer Notice from time
          to time. The current Consumer Notice will be effective when published
          on the date indicated above. When required by applicable law, we will
          notify you of any changes to this Consumer Notice by posting an
          updated Consumer Notice on this website, and will seek your
          affirmative consent to use your personal information for a materially
          different purpose than described at the time the data was collected.
        </p>

        <p>
          <strong>B. Sources of Personal Information</strong>
        </p>
        <p>
          We collect information about you and how you interact with us in
          several ways, including:
        </p>
        <ol className="list-decimal pl-xl">
          <li>
            <strong>Information you provide to us directly.</strong> Our
            website, mobile site, application, and/or other service, regardless
            of how distributed, transmitted, published, or broadcast (each, a
            “Service”) may allow you and/or other users to create, post, store,
            and share content, including but not limited to messages, text,
            photos, graphics, videos, software, code, and other materials
            (collectively, “User Content”). We collect the information you
            provide to us directly, such as when registering and/or set up an
            account/profile to access the Service or Digital Properties,
            including by submitting User Content.
          </li>
          <li>
            <strong>
              Information automatically collected or inferred from your online
              interaction with us.
            </strong>{' '}
            We automatically collect technical information about your online
            interactions with us (such as IP address or other unique identifier
            related to you or your laptop, personal computer, browser, mobile
            device, tablet, or other device (a “Device” or “Devices”), Device
            location, characteristics, and capabilities, prior and current web
            activity, your Carvana or ADESA website activities and related click
            data, and browsing preferences when you visit or interact with our
            Service or Digital Properties (“Usage Information”).
          </li>
          <li>
            <strong>Information from public sources,</strong> including
            government entities from which public records are obtained and
            information you submit in public forums.
          </li>
          <li>
            <strong>Information from third parties.</strong> We receive
            information about you and your interactions with us from third
            parties, such as identity verification partners, marketing and
            advertising providers, social media companies, ad networks, lead
            generators, data brokers, and companies that provide or sell lists
            of potential purchasers.
          </li>
        </ol>
        <p>
          We may combine information that we receive from the various sources
          described in this Consumer Notice, including third party sources and
          public sources, and use or disclose it for the purposes identified
          below.
        </p>

        <p>
          <strong>C. Types of Personal Information We Collect</strong>
        </p>
        <p>
          The types of information that we collect about you (“Personal
          Information”) include:
        </p>
        <ol className="list-decimal pl-xl">
          <li>
            <strong>Identifiers,</strong> such as your name, alias, postal
            address, country of residence, unique personal identifier, online
            identifier, internet protocol address, email address, account name,
            phone number, social media identifiers (e.g., X handle, Instagram
            name, etc.) driver’s license number, passport number, or other
            similar identifiers.
          </li>
          <li>
            <strong>Customer records,</strong> such as signatures.
          </li>
          <li>
            <strong>Protected Class and Demographic information,</strong> such
            as age, race, gender, gender identity, national origin, religion,
            citizenship, marital status, military or veteran status, or date of
            birth.
          </li>
          <li>
            <strong>Commercial information and preferences,</strong> including
            records of personal property, products or services purchased,
            obtained, or considered, information you provide or post using the
            Service or Digital Properties, or other purchasing or consuming
            histories or tendencies.
          </li>
          <li>
            <strong>
              Internet or other electronic network activity information,
            </strong>{' '}
            such as your browsing history, search history, and information
            regarding your interactions with us (including interacting with us
            online, through chat/chatbots, by mobile application, and through
            advertisements).
          </li>
          <li>
            <strong>Geolocation information,</strong> such as approximate
            location based on your IP address.
          </li>
          <li>
            <strong>Audio, electronic, visual, or similar information,</strong>{' '}
            such as video recordings of our premises, call center recordings,
            pictures you may post or submit via the Service or the Digital
            Properties, web activity recordings, or customer support chat logs
            and/or recordings.
          </li>
          <li>
            <strong>Professional or employment information,</strong> such as job
            title, employer, business address and contact information,
            employment history, or other professional information.
          </li>
          <li>
            <strong>Education information,</strong> such as education history,
            including information related to level of education derived from
            credit reports and related data sources.
          </li>
          <li>
            <strong>Inferences</strong> drawn from any of the information we
            collect to create a profile about you reflecting your preferences,
            characteristics, and behaviors.
          </li>
          <li>
            <strong>Sensitive personal information, </strong>such as social
            security number, driver’s license number, or passport number;
            account log-in information; racial or ethnic origin; and precise
            geolocation (with your express consent).
          </li>
        </ol>

        <p>
          <strong>D. How We Use Your Personal Information</strong>
        </p>
        <p>
          We may use each category of your information described above in the
          following ways:
        </p>
        <ol className="list-decimal pl-xl">
          <li>
            <strong>
              To enable interactions between you and us or between you and other
              users or third parties,
            </strong>{' '}
            such as to facilitate online purchases of our products or those
            offered by other users or third parties using the Service or Digital
            Properties; process shipping and returns; register and administer
            your account, provide you with and support your interactions with
            us, other users, and/or third parties; diagnose, repair and track
            service and quality issues; facilitate an order, download, return,
            or exchange; provide requested product information; communicate with
            you about your account or our data practices; install and configure
            changes and updates to programs and technologies related to
            interactions with us; authenticate those who interact with us; or to
            respond to your requests, complaints, and inquiries.
          </li>
          <li>
            <strong>For our own internal business purposes,</strong> such as to
            evaluate, audit, debug, or improve the usage, quality, and
            performance of programs and technologies related to interactions
            with us or third parties; design new services; process and catalog
            your responses to surveys or questionnaires (e.g., customer
            satisfaction reviews); perform internal research for technological
            development and demonstration; conduct data analysis and testing;
            and maintain proper business records and other relevant records.
          </li>
          <li>
            <strong>For legal, safety, or security reasons,</strong> such as to
            comply with legal requirements; protect our safety, our property, or
            rights of those who interact with us, or others; conduct compliance
            training; investigate and respond to legal claims; respond to law
            enforcement requests; and detect, prevent, and respond to security
            incidents or other malicious, deceptive, fraudulent, or illegal
            activity.
          </li>
          <li>
            <strong>For marketing.</strong> We may use your Personal Information
            to market our products or services or those of third parties, such
            as our business partners or other users of our Service. We may use
            your Personal Information to invite you to participate in marketing
            surveys, questionnaires, promotions, events, or contests. We may
            audit aspects of our ad impressions and verify positioning and
            quality of ad impressions. We may use your Personal Information for
            short-term transient use, including for contextual ad customization.
            We may use your Personal Information to enhance our content and to
            deliver advertisements to you, including commercial emails. For more
            information about online marketing activity, see Section F below.
          </li>
          <li>
            <strong>Plaid Technologies, Inc.</strong> (“Plaid”): if you elect to
            link to Plaid during your use of the Service or Digital Properties,
            we may use Plaid to gather certain data from financial institutions.
            By electing to link to Plaid, you grant us and Plaid the right,
            power, and authority to act on your behalf to access and transmit
            your personal and financial information from the relevant financial
            institution. By electing to link to Plaid, you agree to your
            personal and financial information being transferred, stored, and
            processed by us and Plaid in accordance with this Consumer Notice
            and the{' '}
            <a
              href="https://plaid.com/legal"
              className="text-text-primary underline"
              data-adobe-id="plaid-privacy-policy"
              data-analytics-id={null}
              data-testid="plaid-privacy-policy-link"
            >
              Plaid Privacy Policy
            </a>
            .
          </li>
          <li>
            <strong>
              For any other purposes for which you provide consent.
            </strong>
          </li>
        </ol>

        <p>
          <strong>E. With Whom We Share Your Personal Information</strong>
        </p>
        <p>
          We may share your Personal Information with the categories of
          recipients described below:
        </p>
        <ol className="list-decimal pl-xl">
          <li>
            <strong>Affiliates and subsidiaries:</strong> We may share your
            Personal Information within the Carvana or ADESA group of companies,
            which includes parents, corporate affiliates, subsidiaries, business
            units, and other companies that share common ownership for the
            purposes described above.
          </li>
          <li>
            <strong>Service providers:</strong> We may share your Personal
            Information with service providers working on our behalf in order to
            facilitate our interactions with you or request or support our
            relationship with you, such as hosting service providers, IT
            providers, operating systems and platforms, internet service
            providers, analytics companies, and marketing providers (e.g., we
            may share your email address with our outbound email marketing
            provider). We may contract with other companies to provide certain
            services, including identity verification, email distribution,
            market research, promotions management, and payment processing. We
            take reasonable efforts to provide these companies with only the
            information they need to perform services on our behalf.
          </li>
          <li>
            <strong>
              Third parties necessary to complete a transaction or request.
            </strong>{' '}
            We may disclose information to third parties to provide you with
            services or benefits you may request, such as shipping vendors. We
            may also share information with third parties at your direction,
            such as if you direct us to send or receive information from third
            parties using Plaid, or if you interact with other users of the
            Service or Digital Properties.
          </li>
          <li>
            <strong>Business partners and other third parties:</strong> We may
            also provide your Personal Information or provide access to your
            Personal Information to our business partners who may use it for
            their own purposes, such as supplemental product providers,
            including those who offer vehicle accessories, insurance, and other
            partners who work with us on promotional or sponsorship
            opportunities, including co-branded products and services;
            advertisers, ad platforms and networks, and social media platforms;
            lead generators; and third parties whose cookies and tracking tools
            we use as described in Section F (Cookies and Tracking
            Technologies).
          </li>
          <li>
            <strong>For legal, security, and safety purposes:</strong> We may
            share your Personal Information with third parties such as law
            enforcement or other government agencies to comply with law or legal
            requirements; to enforce or apply our{' '}
            <a
              href="https://www.carvana.com/terms-of-use"
              className="text-text-primary underline"
              data-adobe-id="user-agreement-sharing"
              data-analytics-id={null}
              data-testid="user-agreement-link-sharing"
            >
              User Agreement
            </a>{' '}
            and other agreements; and to protect our rights, property, and
            safety, and of our users and third parties.
          </li>
          <li>
            <strong>In connection with a corporate transaction:</strong> We may
            transfer any information we have about you in connection with a
            change in corporate control, including but not limited to a merger
            or sale (including transfers made as part of insolvency or
            bankruptcy proceedings) involving all or part of Carvana or ADESA,
            or as part of a corporate reorganization or stock sale.
          </li>
        </ol>
        <p>
          We may also de-identity, anonymize, or aggregate Personal Information
          to share with third parties for any purpose.
        </p>
        <p>
          We do not knowingly or purposefully engage in the direct selling of
          customer phone number or other related short code data for the
          purposes of direct third party marketing to our customers without
          customers’ express consent, including for the purpose of direct
          marketing campaigns banned by carriers, such as Debt Forgiveness, Work
          and Investment Opportunities, and/or other similar campaigns.
        </p>

        <p>
          <strong>F. How We Use Cookies and Tracking Technologies</strong>
        </p>
        <p>
          We and authorized third parties may also collect information, which
          may include Personal Information, via cookies, web beacons, tracking
          pixels, embedded scripts, location-identifying technologies, Device
          recognition technologies, in-app tracking methods, Device and activity
          monitoring, session replay technologies, or other manual, passive, or
          automatic tracking technologies, programs, or platforms (“Tracking
          Technologies”).
        </p>
        <p>
          <span className="underline">Types of information collected</span>. We
          and authorized third parties collect data about you and your Devices,
          including but not limited to Usage Information; your Internet Service
          Provider; IP address/Internet address from which you accessed our
          website(s) and/or other unique identifier(s) related to you or your
          Device(s); cookie ID; Ad ID; Device location (both approximate and
          precise); Device characteristics (including make, model, operating
          system/settings, and other specifications); browser type used; the
          date and time you access our website(s) and/or take certain actions on
          our website(s); recordings of your interactions with our websites and
          other Digital Properties; the pages you accessed while visiting our
          website(s) and related click behavior (such as products or offerings
          viewed and/or purchased); advertisements viewed; previously visited
          websites, applications, and services; search history; and preferences.
        </p>
        <p>
          <span className="underline">Types of Tracking Technologies used</span>
          . We and third parties that we authorize may use the following
          Tracking Technologies on the Service and Digital Properties:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            <strong>Cookies</strong>
            <p>
              A small text file that is placed and stored on a user’s Device for
              a single session or over multiple sessions over time that help
              help in understanding how you use the Service and Digital
              Properties, for marketing, to personalize and enhance your user
              experience, and other purposes described below.
            </p>
          </li>
          <li>
            <strong>Web Beacons (“Tracking Pixels”)</strong>
            <p>
              Small graphic images, also known as “tracking pixels,” “Internet
              tags,” or “clear gifs,” embedded in web pages and e-mail messages
              that may be used to monitor how users navigate the Service and
              Digital Properties, and to count content views.
            </p>
          </li>
          <li>
            <strong>Embedded Scripts</strong>
            <p>
              Programming code designed to collect information about your
              interactions with the Service and Digital Properties that is
              temporarily downloaded onto your computer from our web server, or
              from a third party with which we work.
            </p>
          </li>
          <li>
            <strong>Location-Identifying Technologies</strong>
            <p>
              Includes GPS (global positioning systems) software, geo-filtering,
              and other location-aware technologies that locate you (sometimes
              precisely), or make assumptions about your location, to verify
              your location and deliver or restrict content based on your
              location. If you have enabled GPS or use other location-based
              features on the Service or Digital Properties, your Device
              location may be tracked by us and third parties. Certain Service
              or Digital Properties features may access and use information
              about your Device location (such as based on IP address or GPS, as
              applicable), or your account information, to suggest certain
              content, products, or services that may be available to you.
            </p>
          </li>
          <li>
            <strong>Device Recognition Technologies</strong>
            <p>
              We employ technologies that identify or infer when multiple
              devices are associated with the same user or household. This can
              be achieved through statistical analysis of usage patterns across
              devices or by linking devices via shared unique identifiers (such
              as a social media login). The resulting “Cross-device Data” helps
              us create a unified view of your digital interactions.
            </p>
          </li>
          <li>
            <strong>In-App Tracking Methods</strong>
            <p>
              Mobile apps use various tracking technologies that aren’t
              controlled by your browser settings, unlike cookies. These
              technologies help us understand how you use our apps and may track
              your activity across different apps and devices. These include “Ad
              IDs” (unique codes linked to your mobile device that help us
              understand your app usage and serve relevant ads) or “Software
              Development Kits (SDKs) (pieces of code from third-party companies
              embedded within our apps. SDKs help us analyze how you interact
              with our app, improve our marketing, and gather information about
              your device and network connection).
            </p>
          </li>
          <li>
            <strong>Device and Activity Monitoring Technologies</strong>
            <p>
              We use technologies like session replay tools to monitor and
              sometimes record your interactions with our Service and Digital
              Properties. This includes how you navigate, your keystrokes, and
              how you engage with content. These tools also collect device
              information such as your operating system, browser plug-ins, and
              system fonts to help us analyze your experience.
            </p>
          </li>
        </ul>
        <p>
          <span className="underline">
            Purposes for using these technologies
          </span>
          . We and authorized third parties use these Tracking Technologies for
          purposes including:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            <strong>Personalization</strong>, such as remembering user
            preferences, login details and browsing behavior; tracking your
            activity across online properties and platforms over time to better
            understand your preferences and interests; and personalizing online
            content;
          </li>
          <li>
            <strong>Improving performance</strong>, such as maintaining and
            improving the performance of the Service and Digital Properties;
          </li>
          <li>
            <strong>Analytics,</strong> such as analyzing how the Service and
            Digital Properties are used to help us improve the Service’s and
            Digital Properties’ performance and user experience;
          </li>
          <li>
            <strong>Advertising</strong>, such as providing you with offers that
            may be of interest to you; conducting targeted advertising to you on
            the Service and Digital Properties, as well as digital properties of
            third parties; and measuring the effectiveness of our communications
            with you and advertising campaigns, including identifying how and
            when you engage with our communications; and
          </li>
          <li>
            <strong>Security</strong>, such as user or Device identification and
            prevention of fraud and malicious behavior.
          </li>
        </ul>
        <p>
          These Tracking Technologies may be used to track you across Devices
          and websites over time for purposes above.
        </p>
        <p>
          <span className="underline">Disclosures of your information</span>. We
          may disclose Personal Information to third parties or permit third
          parties to place Tracking Technologies on our website(s) to allow
          these third parties to directly collect Personal Information from you,
          such as social media companies, advertising networks, companies that
          provide analytics including ad tracking and reporting, security
          providers, and others that help us operate our business, including the
          Service and Digital Properties. These companies use this information
          to provide you with more relevant advertising tailored to your
          interest based on your browsing of the Service and Digital Properties
          and elsewhere on the Internet, sometimes known as “interest-based
          advertising” and “online behavioral advertising (“Interest-based
          Advertising”) which may include sending you an ad on another online
          service after you have left the Service or Digital Properties (i.e.,
          “retargeting”).
        </p>
        <p>
          Our disclosures of information to third parties may include the
          following:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            <strong>Google Analytics. </strong>We use Google Analytics to
            analyze traffic to our website(s) in order to help us understand our
            customers’ and visitors’ needs and to continually improve our
            site(s) for them. The data they collect on our behalf may include,
            but is not limited to, search engine referral, affiliate referrals,
            traffic driven by banner ads or other online promotions, how
            visitors navigate around the site, and the most popular pages. We
            also collect certain technical information, such as the browser
            version and operating system. We reserve the right to use, transfer,
            sell, and share data about our users for any lawful business
            purpose, such as analyzing usage trends and seeking compatible
            advertisers, sponsors, clients, and customers. For more information
            on how Google Analytics collects and processes data, please visit{' '}
            <a
              href="http://www.google.com/policies/privacy/partners/"
              className="text-text-primary underline"
              data-adobe-id="google-analytics-partners"
              data-analytics-id={null}
              data-testid="google-analytics-partners-link"
            >
              www.google.com/policies/privacy/partners/
            </a>
            .
          </li>
          <li>
            <strong>Social media companies. </strong>We use social media
            Tracking Technologies (such as the Meta Pixel and/or related Meta
            business tools) that may allow third parties, including Meta, to
            collect or receive information from our Service, Digital Properties,
            and/or elsewhere on the Internet and use that information to provide
            measurement services and target ads. In addition, we may serve ads
            on other online services that are targeted to reach people on those
            services that are also identified in one or more of our databases
            (“Matched List Ads”). This is done by using Tracking Technologies or
            by matching common factors between our databases and the databases
            of the other online services. For instance, we may use such ad
            services offered by Meta or X and other third-party services. We are
            not responsible for these third-party services, including without
            limitation their security of the data. Most third party social media
            companies (such as Meta and X) will generally enable you to hover
            over the box in the right corner of such a Meta ad, or go to your
            social media account settings, and find out what options you have to
            control such ads. We are not responsible for such third parties’
            failure to comply with your or our opt-out instructions, they may
            not give us notice of opt-outs to our ads that you give to them, and
            they may change their options without notice to us or you.
          </li>
        </ul>
        <p>
          <span className="underline">
            Your choices regarding Tracking Technologies
          </span>
          . Some of the companies that perform advertising-related services may
          participate in the Digital Advertising Alliance’s (“DAA”)
          Self-Regulatory Program for Online Behavioral Advertising. To learn
          more about how you can exercise certain choices regarding
          Interest-based Advertising, visit{' '}
          <a
            href="http://www.aboutads.info/choices/"
            className="text-text-primary underline"
            data-adobe-id="daa-choices"
            data-analytics-id={null}
            data-testid="daa-choices-link"
          >
            http://www.aboutads.info/choices/
          </a>{' '}
          and{' '}
          <a
            href="http://www.aboutads.info/appchoices"
            className="text-text-primary underline"
            data-adobe-id="daa-appchoices"
            data-analytics-id={null}
            data-testid="daa-appchoices-link"
          >
            http://www.aboutads.info/appchoices
          </a>{' '}
          for information on the DAA’s opt-out programs. Some of these companies
          may also be members of the Network Advertising Initiative (“NAI”). For
          more information about the NAI including how consumers can opt-out
          from receiving interest-based advertising from some or all of NAI’s
          members, please visit{' '}
          <a
            href="http://www.networkadvertising.org/choices"
            className="text-text-primary underline"
            data-adobe-id="nai-choices"
            data-analytics-id={null}
            data-testid="nai-choices-link"
          >
            http://www.networkadvertising.org/choices
          </a>
          . Opting out of Interest-based Advertising will not opt you out of all
          advertising, but rather only Interest-based Advertising from us or our
          agents or representatives. Also, if your browsers are configured to
          reject cookies when you visit these opt-out webpages, or you
          subsequently erase your cookies, use a different Device or web
          browser, or use a non-browser-based method of access (e.g., mobile
          app), your NAI / DAA browser-based opt-out may not, or may no longer,
          be effective.
        </p>
        <p>
          Some browsers have incorporated Do Not Track (“DNT”) preferences. Most
          of these features, when turned on, send signals to the website you are
          visiting that you do not wish to have information about your online
          searching and browsing activities collected and used. As there is not
          yet a common agreement about how to interpret DNT signals, we do not
          honor DNT signals from website browsers at this time. However, you may
          be able to refuse or delete cookies. If you refuse or delete cookies,
          some of our website(s) functionality may be impaired. If you change
          Devices or browsers, or use multiple Devices or browsers, and delete
          your cookies, you may need to repeat this process for each Device or
          browser. Please refer to your browser’s Help instructions to learn
          more about how to manage cookies and the use of other tracking
          technologies.
        </p>

        <p>
          <strong>G. Security </strong>
        </p>
        <p>
          We maintain reasonable security procedures and technical and
          organizational measures to protect your Personal Information against
          accidental or unlawful destruction, loss, disclosure, alteration, or
          use. However, because no data transmission is completely secure, and
          no system of physical or electronic security is impenetrable, we
          cannot guarantee the security of the information you send to us or the
          security of our servers, networks, or databases, and by using our
          Service or Digital Properties you agree to assume all risk in
          connection with the information sent to us or collected by us when you
          access, visit, and/or use our Service or Digital Properties, including
          without limitation your personally identifiable information, Personal
          Information, or other registration information, and we are not
          responsible for any loss of such information, or the consequences
          thereof.
        </p>

        <p>
          <strong>H. Children’s Privacy</strong>
        </p>
        <p>
          Interactions with us are intended for individuals 16 years of age and
          older. Our interactions are not directed at, marketed to, nor intended
          for, children under 16 years of age. We do not knowingly collect any
          information, including Personal Information, from children under 16
          years of age. If you believe that we have inadvertently collected
          Personal Information from a child under the age of 16, please contact
          us at the address below and we will use reasonable efforts to delete
          the child’s information from our databases.
        </p>

        <p>
          <strong>I. External Links</strong>
        </p>
        <p>
          When interacting with us you may encounter links to external sites or
          other online services, including those embedded in third party
          advertisements or sponsor information, that we do not control. We are
          not responsible for the privacy practices and data collection policies
          for such third party services. You should consult the privacy
          statements of those third party services for details.
        </p>

        <p>
          <strong>J. User Agreement</strong>
        </p>
        <p>
          The{' '}
          <a
            href="https://www.carvana.com/terms-of-use"
            className="text-text-primary underline"
            data-adobe-id="user-agreement-incorporation"
            data-analytics-id={null}
            data-testid="user-agreement-link-incorporation"
          >
            User Agreement
          </a>{' '}
          for interactions with us is incorporated by reference into this
          Consumer Notice.
        </p>

        <p>
          <strong>K. Contact Info/Your Choices</strong>
        </p>
        <p>
          If you have questions regarding this Consumer Notice, please contact
          us at:
        </p>

        <Table>
          <tbody>
            <tr>
              <TableCell>EMAIL:</TableCell>
              <TableCell>privacypolicy@carvana.com</TableCell>
            </tr>
            <tr>
              <TableCell>MAIL:</TableCell>
              <TableCell>
                Attention: Legal
                <p>300 E Rio Salado Pkwy</p>
                <p>Building 1</p>
                <p>Tempe, AZ 85281</p>
              </TableCell>
            </tr>
          </tbody>
        </Table>

        <p>
          Note: Only inquiries involving this Consumer Notice or Carvana’s
          collection, use, and/or management of your personal information should
          be sent to this email and/or mailing address. No other communication
          will be accepted or addressed.
        </p>
        <p>
          <span className="underline">Updating your information</span>. If
          you’ve registered on any part of the Service or Digital Properties,
          you may change or update your member preferences or information in
          your account settings, if available, to keep all such data accurate
          and up to date.
        </p>
        <p>
          <strong>Unsubscribing from promotional email messages. </strong>To
          opt-out of receiving promotional email messages from us, please click
          on the “Unsubscribe” link contained at the bottom of each email or by
          contacting us using the information above.
        </p>
        <p>
          <strong>Android Account Deletion. </strong>If you are an Android app
          user, please{' '}
          <a
            href="https://www.carvana.com/privacy-policy/android-delete"
            className="text-text-primary underline"
            data-adobe-id="android-account-deletion"
            data-analytics-id={null}
            data-testid="android-account-deletion-link"
          >
            click here
          </a>{' '}
          to submit a request to delete your account.
        </p>

        <p id="l-your-california-privacy-rights">
          <strong>
            L. Your California, Connecticut, Minnesota, Montana, and Oregon
            (“Applicable Privacy States”) Privacy Rights
          </strong>
        </p>
        <p>
          This section applies to certain California, Connecticut, Minnesota,
          Montana, and Oregon residents (“Applicable Privacy State Resident(s)”)
          and supplements (but does not replace) the Consumer Notice above. This
          section does not apply to the following information:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            Information about individuals who are not Applicable Privacy State
            Residents.
          </li>
          <li>
            Information we collect in connection with providing individual
            consumers a financial product or service (or in connection with a
            consumer seeking such financial products or services), such as by
            reviewing applications for financing. For example, this Consumer
            Notice does not apply to information we collect that is subject to
            the federal Gramm-Leach-Bliley Act (“GLBA”), Fair Credit Reporting
            Act (“FCRA”), or the California Financial Information Privacy Act
            (collectively, “Financial Information”). Financial Information is
            subject instead to our{' '}
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financial-and-other-privacy-notices`,
                dealershipId
              )}
              className="text-text-primary underline"
              data-adobe-id="financial-privacy-notice-l"
              data-analytics-id={null}
              data-testid="financial-privacy-notice-link-l"
            >
              Financial Privacy Notice
            </a>
            .
          </li>
          <li>
            Information about job applicants. Such information is subject to a
            separate{' '}
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financial-and-other-privacy-notices#applicant-privacy-notice`,
                dealershipId
              )}
              className="text-text-primary underline"
              data-adobe-id="applicant-privacy-notice-l"
              data-analytics-id={null}
              data-testid="applicant-privacy-notice-link-l"
            >
              Applicant Privacy Notice
            </a>
            .
          </li>
          <li>
            Information about our employees, contractors, and agents. Such
            information is subject to a separate employee privacy notice that we
            will make available to those individuals.
          </li>
          <li>
            Information we collect from individuals with whom we engage in
            solely business-to-business communications and transactions,
            including due diligence transactions, such as information about the
            employees of our business clients. Such information is subject to a
            separate{' '}
            <a
              href={buildDealershipUrl(
                `${absoluteBaseUrl}/financial-and-other-privacy-notices#business-contact-privacy-notice`,
                dealershipId
              )}
              className="text-text-primary underline"
              data-adobe-id="business-contact-privacy-notice-l"
              data-analytics-id={null}
              data-testid="business-contact-privacy-notice-link-l"
            >
              Business Privacy Notice
            </a>
            .
          </li>
        </ul>
        <p>
          Pursuant to the Applicable State laws, below is a summary of the
          personal information we have collected from consumers, the parties
          with whom we may have disclosed the personal information for business
          purposes, and the parties to whom we may have sold or shared the
          personal information over the last twelve (12) months. We do not
          knowingly sell the personal information of minors under 16 years of
          age.
        </p>

        <Table>
          <thead>
            <tr>
              <TableHeaderCell>
                Categories of Personal Information We Collect
              </TableHeaderCell>
              <TableHeaderCell>
                Categories of Third Parties With Whom We Disclose Personal
                Information for a Business Purpose
              </TableHeaderCell>
              <TableHeaderCell>
                Categories of Third Parties With Whom We Sell or Share Personal
                Information
              </TableHeaderCell>
            </tr>
          </thead>
          <tbody>
            <tr>
              <TableCell>
                <span className="underline">Identifiers</span>, such as real
                name, alias, postal address, country of residence, unique
                personal identifier, online identifier, IP address, email
                address, account name, phone number, social media identifiers,
                or other similar identifiers.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    technical support, research and analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as those who offer
                    vehicle accessories, insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">
                  Personal information subject to the California Customer
                  Records Act
                </span>
                , such as signatures.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    technical support, research and analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">
                  Characteristics of protected classifications under California
                  or Federal Law
                </span>
                , such as age, sex (including gender, gender identity, gender
                expression), national origin, citizenship, religion or creed,
                marital status, education, date of birth, veteran or military
                status.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    technical support, research and analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies to whom your data is shared at your direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">Commercial information</span>,
                including records of personal property, product or services
                purchased, obtained or considered, information you provide or
                post using the Service, or other purchasing or consuming
                histories or tendencies.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    technical support, research and analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">
                  Internet or other electronic network activity information
                </span>
                , including, but not limited to, browsing history, search
                history, and information regarding a consumer’s interaction with
                an internet website, application or advertisement.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">Geolocation data</span>, such as
                approximate location of a Device or equipment based on
                geographical coordinates and measurements,
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">
                  Audio, electronic, visual, or similar information
                </span>{' '}
                such as CCTV footage, call recordings, pictures you post or
                submit via the Service or Digital Properties, customer support
                chat recordings, and web activity recordings, etc.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">
                  Professional or employment-related information
                </span>
                , such as job title, employer, business address and contact
                information, employment history, other professional information,
                or education history.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">Education information</span> that is
                not publicly available personally identifiable information as
                defined in the Family Educational Rights and Privacy Act.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">Inferences</span> drawn from any of
                the information identified above to create a profile about a
                consumer reflecting the consumer’s preferences, characteristics,
                and behavior.
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Third party social network services</li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>Marketing and advertising partners or providers</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>
                    Supplemental product providers, such as vehicle accessories,
                    insurance, etc.
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Third party social network services</li>
                  <li>Data analytics providers</li>
                  <li>Companies that create or receive sales leads</li>
                </ul>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <span className="underline">
                  Sensitive personal information
                </span>
                , such as social security number, driver’s license number or
                passport number; account log-in information; racial or ethnic
                origin or religious or philosophical beliefs; and precise
                geolocation
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>
                    Service providers, such as identity verification services,
                    financial services, technical support, research and
                    analytics providers, etc.
                  </li>
                  <li>Our parents, subsidiaries, and affiliates</li>
                  <li>
                    Public or law enforcement officials, as well as private
                    parties involved in other legal process
                  </li>
                  <li>Consumer credit reporting companies</li>
                  <li>
                    Companies or other users to whom your data is shared at your
                    direction
                  </li>
                  <li>Entities involved in a corporate transaction</li>
                </ul>
              </TableCell>
              <TableCell>
                <ul className="list-disc pl-xl">
                  <li>Not sold or shared</li>
                </ul>
              </TableCell>
            </tr>
          </tbody>
        </Table>

        <p>
          Collection of the categories of personal information identified above
          may occur through one or more of the following sources:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            From you, when you engage in certain activities such as when you
            register and/or set up an account/profile to access the Service or
            Digital Properties, including by submitting User Content.
          </li>
          <li>
            From third party social networking service(s) that you may have
            accessed, visited, and/or used that may be integrated with or linked
            to the Service or Digital Properties(s).
          </li>
          <li>From lead generation partners or sources.</li>
          <li>From identity verification partners.</li>
          <li>
            From entities that sell or share your personal information,
            including entities registered in California as data brokers.
          </li>
          <li>
            From service providers who gather personal information from you or
            external parties who possess and/or process your personal
            information, once you grant the requisite right, power, and/or
            authority to those service providers to act on your behalf and
            gather and/or provide such data to us.
          </li>
          <li>
            Through local device storage (placing and/or storing code or other
            types of information and/or devices (e.g., “cookies”) on your
            Device(s)) or other Tracking Technologies (web beacons, web bugs,
            clear gifs, and similar technologies), as described in detail in
            Section B and F of this Consumer Notice.
          </li>
        </ul>
        <p>
          The business and/or or commercial purpose(s) for which we collect the
          data identified above may include one or more of the following:
        </p>
        <ul className="list-disc pl-xl">
          <li>
            Auditing, such as auditing related to a current interaction with you
            and concurrent transactions, including, but not limited to, counting
            ad impressions to unique visitors, verifying positioning and quality
            of ad impressions, and auditing compliance with this specification
            and other standards;
          </li>
          <li>
            Debugging, such as debugging to identify and repair errors that
            impair existing intended functionality;
          </li>
          <li>
            The occurrence of mergers and acquisitions, including in the event
            Carvana or ADESA undergoes a corporate reorganization such as a
            merger, acquisition, sale of part or all of its business, or other
            related event (including transfers made as part of insolvency or
            bankruptcy proceedings) involving all or part of Carvana or ADESA,
            or as part of a corporate reorganization or stock sale, in which
            data may be collected, used, or shared for the purpose of
            negotiating or completing such transactions;
          </li>
          <li>
            Performing or improving services, such as maintaining or servicing
            accounts, providing customer service, facilitating communications
            between users, providing consumers with requested or personalized
            content, communicating with you about new features, products, or
            services, processing or fulfilling orders and transactions,
            tailoring the services that we offer to your needs; verifying your
            information, processing payments, providing financing, providing
            analytic services, or providing similar services on behalf of
            Carvana or ADESA;
          </li>
          <li>
            Quality assurance, including undertaking activities to verify or
            maintain the quality or safety of a service or device that is owned,
            manufactured, manufactured for, or controlled by Carvana or ADESA,
            and to improve, upgrade, or enhance the Service and Digital
            Properties or a device that is owned, manufactured, manufactured
            for, or controlled by Carvana or ADESA;
          </li>
          <li>
            Security, such as detecting security incidents, protecting against
            malicious, deceptive, fraudulent, or illegal activity, and/or
            prosecuting those responsible for that activity;
          </li>
          <li>Short-term, transient use;</li>
          <li>
            To communicate, advertise, and/or market Carvana or ADESA’s goods,
            products, and/or services, to show you content and sponsored
            messaging, to engage in interest-based advertising, to gauge the
            effectiveness of marketing campaigns, and/or for other marketing or
            advertising purposes;
          </li>
          <li>
            To comply with legal or compliance requirements, such as compliance
            training, investigating and responding to legal claims, responding
            to law enforcement requests, or as otherwise required by law;
          </li>
          <li>
            To conduct internal research, including undertaking internal
            research for technological development and/or demonstration and
            analyzing trends;
          </li>
          <li>
            To help diagnose problems with our servers, to gather broad
            demographic information, track users’ movements around the Service
            and Digital Properties, and/or to otherwise administer the Service
            and Digital Properties; and/or
          </li>
          <li>
            To track geographic location, usage patterns, broad demographic
            information, and/or the movements of individual users.
          </li>
        </ul>

        <p>
          <span className="underline">Sensitive Personal Information.</span> We
          only use and disclose Sensitive Personal Information for the following
          purposes: (i) performing services or providing goods reasonably
          expected by an average consumer; (ii) detecting security incidents;
          (iii) resisting malicious, deceptive, or illegal actions; (iv)
          ensuring the physical safety of individuals; (v) for short-term,
          transient use, including non-personalized advertising; (vi) performing
          or providing internal business services; (vii) verifying or
          maintaining the quality or safety of a service or device; or (viii)
          for purposes that do not infer characteristics about you.
        </p>

        <p>
          <span className="underline">Retention</span>. We retain your Personal
          Information as long as necessary to fulfill the purposes outlined in
          this Consumer Notice unless we are required to do otherwise by
          applicable law. This includes retaining your Personal Information to
          provide you with the products or services you have requested and
          interact with you; maintain our business relationship with you;
          improve our business over time; ensure the ongoing legality, safety
          and security of our services and relationships; or otherwise in
          accordance with our internal retention procedures. Once you have
          terminated your relationship with us, we may retain your Personal
          Information in our systems and records in order to ensure adequate
          fulfillment of surviving provisions in terminated contracts or for
          other legitimate business purposes, such as to enable easier future
          user onboarding, demonstrate our business practices and contractual
          obligations, or provide you with information about our products and
          services in case of interest.
        </p>
        <p id="consumer-privacy-notice-your-rights">
          <span className="underline">Your Rights</span>
        </p>
        <p>
          Applicable Privacy State Residents may exercise the following rights,
          subject to legal limitations and applicable exceptions, via our
          Privacy Request Form at{' '}
          <a
            href={complianceDsarPageUrl}
            className="text-text-primary underline"
            data-adobe-id="privacy-request-form-your-rights"
            data-analytics-id={null}
            data-testid="privacy-request-form-link-your-rights"
          >
            {complianceDsarPageUrl}
          </a>{' '}
          or by calling 800-333-4554.
        </p>
        <ul className="list-disc pl-xl">
          <li>
            <strong>Right to Know.</strong> You have the right to request
            information about the categories of personal information we have
            collected about you, the categories of sources from which we
            collected the personal information, the purposes for collecting,
            selling, or sharing the personal information, and the categories of
            third parties with whom we have shared or sold your personal
            information (“Categories Report”). You may also request information
            about the specific pieces of personal information we have collected
            about you (“Specific Pieces Report”). Oregon and Minnesota residents
            may also have the right to request a list of specific third parties
            to which we have disclosed any personal information (“Right to Know
            - Third Parties”).
          </li>
          <li>
            <strong>Right to Delete.</strong> You have the right to request that
            we delete personal information that we have collected from you.
          </li>
          <li>
            <strong>Right to Correct</strong>. You have the right to request
            that we correct inaccurate information that we maintain about you.
          </li>
          <li>
            <strong>
              Right to Opt Out of Sales, Sharing, and Targeted Advertising.
            </strong>{' '}
            You have a few ways to opt out of the “selling,” “sharing,” and
            “targeted advertising” of your personal information. Please choose
            the option that works best for you.
            <ul className="list-disc pl-xl">
              <li>
                <strong>Submit a Full Request (Recommended)</strong> - To opt
                out across your account and devices, please complete and submit
                our{' '}
                <a
                  href={complianceDsarPageUrl}
                  className="text-text-primary underline"
                  data-adobe-id="privacy-request-form-full-request"
                  data-analytics-id={null}
                  data-testid="privacy-request-form-link-full-request"
                >
                  Privacy Request Form
                </a>
                .
              </li>
              <li>
                <strong>Use Our Browser-Only Tool</strong> - If you prefer not
                to provide contact information, you can opt out on this browser
                only by clicking{' '}
                <a
                  // type="button"
                  onClick={openPrivacyPreferences}
                  className="inline appearance-none border-0 bg-transparent p-0 text-text-primary underline"
                  data-adobe-id="browser-only-opt-out"
                  data-analytics-id="Privacy Policy - Browser-Only Opt-Out"
                  data-testid="browser-only-opt-out"
                >
                  here
                </a>{' '}
                and selecting “Opt-Out.”
              </li>
              <li>
                <strong>Use Your Account Settings</strong> - If you have a
                Carvana account, you can also manage your choices at any time by
                visiting your Account Settings and selecting the Privacy
                Settings tab.
              </li>
            </ul>
          </li>
        </ul>

        <p>
          To the extent required by law, we will honor opt-out preference
          signals sent in a format commonly used and recognized by businesses,
          such as an HTTP header field or JavaScript object. We will process
          opt-out preference signals at the browser level.
        </p>
        <p>
          In accordance with applicable law, we will not discriminate against
          you for exercising these rights.
        </p>

        <div>
          <p>
            In order to exercise your rights, we need to obtain information
            about you:
          </p>
          <ul className="list-disc pl-xl">
            <li>
              <strong>For Requests to Opt-Out:</strong> We collect your name and
              email in order to locate you in our records.
            </li>
            <li>
              <strong>For Requests to Know, Delete, and Correct:</strong> We
              collect information necessary to verify your identity and that you
              are an Applicable Privacy State Resident, including name, mailing
              address, email address, phone number, and relationship to Carvana.
              Once you submit a request, we will send an email to acknowledge
              our receipt of your request and provide a verification link you
              must click in order for us to process your request.
            </li>
          </ul>
        </div>

        <p>
          Please be aware there might be circumstances where we will not honor
          your request, as permitted under the Applicable Privacy State laws.
          For example, if we are not able to verify your identity and/or that
          you are an Applicable Privacy State Resident, we may not honor your
          access, deletion, or correction request. Additionally, we may not
          honor your request if an exception under the Applicable Privacy State
          laws apply.
        </p>
        <p>
          <span className="underline">
            Connecticut, Oregon, Minnesota, and Montana Appeal Rights
          </span>
          : If you’re a Connecticut, Oregon, Minnesota, or Montana resident and
          your privacy rights request is denied or Carvana refuses to take
          action on your request, you may appeal Carvana’s decision. To exercise
          your right to appeal, please email privacypolicy@carvana.com within 30
          calendar days following the denial of your request with the following
          information:
        </p>
        <div>
          <ol className="list-decimal pl-xl">
            <li>
              Please put “Privacy Request Appeal” in the subject line of the
              email.
            </li>
            <li>
              In the body of the email, please provide:
              <ol className="list-[lower-alpha] pl-xl">
                <li>Your name;</li>
                <li>
                  Request Type (e.g., “Right to Delete,”; “Right to Correct”);
                  and
                </li>
                <li>
                  A brief statement explaining why you believe your original
                  request was improperly denied.
                </li>
              </ol>
            </li>
          </ol>
          <p>
            Carvana will respond to your appeal within 45 days of receipt if
            you’re a resident of Oregon or Minnesota and within 60 days if
            you’re a resident of Connecticut or Montana.
          </p>
        </div>

        <p>
          <span className="underline">Authorized Agents:</span> Authorized
          agents must use the{' '}
          <a
            href={complianceDsarPageUrl}
            className="text-text-primary underline"
            data-adobe-id="privacy-request-form-authorized-agents"
            data-analytics-id={null}
            data-testid="privacy-request-form-link-authorized-agents"
          >
            Privacy Request Form
          </a>{' '}
          to exercise rights on a consumer’s behalf. If you designate an
          authorized agent to submit a Right to Know, Right to Delete, or Right
          to Correct request, we may reach out to you directly to verify your
          own identity or to confirm that you provided the authorized agent
          permission to submit the request. If you designate an authorized agent
          to submit a Right to Opt Out of Sale, Sharing, and Targeted
          Advertising request, we may seek additional information directly from
          the authorized agent to process the request
        </p>
        <p>
          <span className="underline">Timing</span>: We will process a Request
          to Opt Out within 15 business days. We will respond to Requests to
          Delete, Requests to Correct, and Requests to Know within 45 calendar
          days, unless we need more time (in which case we will notify you) and
          may take up to 90 calendar days in total to respond to your
          request(s).
        </p>
        <p>
          <span className="underline">California Shine the Light</span>: If you
          are a California resident, you may opt out of sharing your Personal
          Information with third parties for the third parties’ direct marketing
          purposes. To do so, please click the following link:{' '}
          <a
            href={complianceDsarPageUrl}
            className="text-text-primary underline"
            data-adobe-id="do-not-sell"
            data-analytics-id={null}
            data-testid="do-not-sell-link"
          >
            Do Not Sell or Share My Personal Information
          </a>
          .
        </p>

        <Table>
          <tbody>
            <tr>
              <TableCell>
                Total Requests Received (1/1/2025 - 12/31/2025)
              </TableCell>
              <TableCell> </TableCell>
            </tr>
            <tr>
              <TableCell>Request Type</TableCell>
              <TableCell>Number of Requests</TableCell>
            </tr>
            <tr>
              <TableCell>
                <strong>Total Do Not Sell Requests Received*</strong>
              </TableCell>
              <TableCell className="text-right">
                <strong>7,031,999</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>Do Not Sell requests denied</TableCell>
              <TableCell className="text-right">127</TableCell>
            </tr>
            <tr>
              <TableCell>
                Do Not Sell requests complied with <em>in whole</em>
              </TableCell>
              <TableCell className="text-right">7,031,872</TableCell>
            </tr>
            <tr>
              <TableCell>
                Do Not Sell requests complied with <em>in part</em>
              </TableCell>
              <TableCell className="text-right">0</TableCell>
            </tr>
            <tr>
              <TableCell>
                Average number of days to complete Do Not Sell requests
              </TableCell>
              <TableCell className="text-right">1</TableCell>
            </tr>
            <tr>
              <TableCell>
                <strong>Total Delete Requests Received</strong>
              </TableCell>
              <TableCell className="text-right">
                <strong>757</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>Delete requests denied</TableCell>
              <TableCell className="text-right">141</TableCell>
            </tr>
            <tr>
              <TableCell>
                Delete requests complied with <em>in whole</em>
              </TableCell>
              <TableCell className="text-right">0</TableCell>
            </tr>
            <tr>
              <TableCell>
                Delete requests complied with <em>in part</em>
              </TableCell>
              <TableCell className="text-right">616</TableCell>
            </tr>
            <tr>
              <TableCell>
                Average number of days to complete Delete requests
              </TableCell>
              <TableCell className="text-right">14</TableCell>
            </tr>
            <tr>
              <TableCell>
                <strong>Total Right to Know Requests Received</strong>
              </TableCell>
              <TableCell className="text-right">
                <strong>33</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>Right to Know requests denied</TableCell>
              <TableCell className="text-right">0</TableCell>
            </tr>
            <tr>
              <TableCell>
                Right to Know requests complied with <em>in whole</em>
              </TableCell>
              <TableCell className="text-right">33</TableCell>
            </tr>
            <tr>
              <TableCell>
                Right to Know requests complied with <em>in part</em>
              </TableCell>
              <TableCell className="text-right">0</TableCell>
            </tr>
            <tr>
              <TableCell>
                Average number of days to complete Right to Know requests
              </TableCell>
              <TableCell className="text-right">36</TableCell>
            </tr>
            <tr>
              <TableCell>
                <strong>Total Right to Correct</strong>
              </TableCell>
              <TableCell className="text-right">
                <strong>0</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <strong>Right to Correct requests denied</strong>
              </TableCell>
              <TableCell className="text-right">
                <strong>0</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>Right to Correct requests completed</TableCell>
              <TableCell className="text-right">
                <strong>0</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                Average Days to complete Right to Correct requests
              </TableCell>
              <TableCell className="text-right">
                <strong>Not applicable</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell>
                <strong>Total Right to Limit Requests</strong>
              </TableCell>
              <TableCell className="text-right">
                <strong>Not applicable</strong>
              </TableCell>
            </tr>
            <tr>
              <TableCell colSpan={2}>
                *Metrics disclosed for this category represent requests for all
                individuals, including California consumers.
              </TableCell>
            </tr>
          </tbody>
        </Table>

        <p>* * * * * * * * * * * * *</p>
      </section>
    </Wrapper>
  );
}
